/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { Draft } from 'immer';
import { InputState, UnknownInputValue } from '../backendModels/general.model';
import {
  GenericElementEnteredValue,
  GenericFieldValueTypes,
  MultiSelectFieldValue,
} from '../backendModels/generic-element.model';
import _ from 'lodash';
import { isGenericRecordDeletable } from '../models/generic';
import { NodeType } from '../backendModels/report.model';
import { AdaptRecordFunction } from '../provider/ReportsAPIProvider';
import { NodeRecordTypeMap } from '../backendModels/records.model';

type RecordWithTouched = (
  | (GenericElementEnteredValue & { values?: { touched?: MultiSelectFieldValue } })
  | (UnknownInputValue & { values?: null })
) &
  NodeRecordTypeMap['generic'];

// Update a text field in the given draft record with value if it is currently untouched and empty.
// Sets the property in synchronizedDataUpdate if either the draft record was updated or the value is already the
// identical to the value in the draft.
export function handleUntouchedTextFieldUpdates<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(
  draft: RECORD_TYPE,
  property: FIELD_KEY,
  value: string | null | undefined,
  synchronizedDataUpdate?: { [field in FIELD_KEY]?: string | null }
): void {
  const prop = draft.values?.[property];

  if (!isPropertyTouched(draft, property) && (prop == null || prop.fieldType !== 'text' || prop.text == null)) {
    if (value != null && value !== '') {
      writeTextFieldValue(draft, property, value);
    } else {
      removeField(draft, property);
    }
    if (synchronizedDataUpdate) {
      synchronizedDataUpdate[property] = value;
    }
  } else if ((prop?.fieldType === 'text' && prop.text === value) || (prop == null && value == null)) {
    if (synchronizedDataUpdate) {
      synchronizedDataUpdate[property] = value;
    }
  }
}

export function overwriteTextField<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, value: string | null | undefined, diff: [string, unknown][]): void {
  if (diff.some((element) => element[0] === property)) {
    if (value != null && value !== '') {
      writeTextFieldValue(draft, property, value);
      removeFromTouchedFields(draft, property);
    } else {
      removeField(draft, property);
    }
  }
}

// Update a numeric field in the given draft record with value if it is currently untouched and empty.
// Sets the property in synchronizedDataUpdate if either the draft record was updated or the value is already the
// identical to the value in the draft.
export function handleUntouchedNumericFieldUpdates<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(
  draft: RECORD_TYPE,
  property: FIELD_KEY,
  value: number | null | undefined,
  synchronizedDataUpdate?: { [field in FIELD_KEY]?: number | null }
): void {
  const prop = draft.values?.[property];
  if (!isPropertyTouched(draft, property) && (prop == null || prop.fieldType !== 'numeric' || prop.number == null)) {
    if (value != null) {
      writeNumericFieldValue(draft, property, value);
    } else {
      removeField(draft, property);
    }
    if (synchronizedDataUpdate) {
      synchronizedDataUpdate[property] = value;
    }
  } else if ((prop?.fieldType === 'numeric' && prop.number === value) || (prop == null && value == null)) {
    if (synchronizedDataUpdate) {
      synchronizedDataUpdate[property] = value;
    }
  }
}

export function overwriteNumericField<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, value: number | null | undefined, diff: [string, unknown][]): void {
  if (diff.some((element) => element[0] === property)) {
    if (value != null) {
      writeNumericFieldValue(draft, property, value);
      removeFromTouchedFields(draft, property);
    } else {
      removeField(draft, property);
    }
  }
}

// Update a singleSelect field in the given draft record with value if it is currently untouched and empty.
// Sets the property in synchronizedDataUpdate if either the draft record was updated or the value is already the
// identical to the value in the draft.
export function handleUntouchedSingleSelectFieldUpdates<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(
  draft: RECORD_TYPE,
  property: FIELD_KEY,
  value: string | null | undefined,
  synchronizedDataUpdate?: { [field in FIELD_KEY]?: string | null }
): void {
  const prop = draft.values?.[property];
  if (
    !isPropertyTouched(draft, property) &&
    (prop == null || prop.fieldType !== 'singleSelect' || prop.option == null)
  ) {
    if (value != null && value !== '') {
      writeSingleSelectFieldValue(draft, property, value);
    } else {
      removeField(draft, property);
    }
    if (synchronizedDataUpdate) {
      synchronizedDataUpdate[property] = value;
    }
  } else if ((prop?.fieldType === 'singleSelect' && prop.option === value) || (prop == null && value == null)) {
    if (synchronizedDataUpdate) {
      synchronizedDataUpdate[property] = value;
    }
  }
}

export function overwriteSingleSelectField<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, value: string | null | undefined, diff: [string, unknown][]): void {
  if (diff.some((element) => element[0] === property)) {
    if (value != null && value !== '') {
      writeSingleSelectFieldValue(draft, property, value);
      removeFromTouchedFields(draft, property);
    } else {
      removeField(draft, property);
    }
  }
}

// Update a multiSelect field in the given draft record with value if it is currently untouched.
export function handleUntouchedMultiSelectFieldUpdates<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(
  draft: RECORD_TYPE,
  property: FIELD_KEY,
  values: readonly string[] | null | undefined,
  localStoragePatient?: { [field in FIELD_KEY]?: readonly string[] | null }
): void {
  const prop = draft.values?.[property];
  if (
    !isPropertyTouched(draft, property) &&
    (prop == null || prop.fieldType !== 'multiSelect' || prop.options == null)
  ) {
    if (values != null) {
      writeMultiSelectFieldValue(draft, property, values);
    } else {
      removeField(draft, property);
    }
    if (localStoragePatient) {
      localStoragePatient[property] = values;
    }
  } else if (prop?.fieldType === 'multiSelect' && _.isEqual(_.sortBy(prop.options), _.sortBy(values))) {
    if (localStoragePatient) {
      localStoragePatient[property] = values;
    }
  }
}

export function overwriteMultiSelectField<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(
  draft: RECORD_TYPE,
  property: FIELD_KEY,
  values: readonly string[] | null | undefined,
  diff: [string, unknown][]
): void {
  if (diff.some((element) => element[0] === property)) {
    if (values != null && values.length > 0) {
      writeMultiSelectFieldValue(draft, property, values);
      removeFromTouchedFields(draft, property);
    } else {
      removeField(draft, property);
    }
  }
}

// Set the given property to the given value and mark it as touched.
// Will also delete the record if isGenericRecordDeletable.
export function changePropertyAndMarkTouched(
  adaptRecord: AdaptRecordFunction,
  nodeType: NodeType,
  elementKey: string,
  property: string,
  value?: GenericFieldValueTypes
) {
  adaptRecord(
    'generic',
    nodeType,
    (draft: Draft<RecordWithTouched>, deleteRecord) => {
      draft.inputState = InputState.ENTERED;

      if (!_.isEqual(value, draft.values?.[property])) {
        draft.values = {
          ...draft.values,
          touched: { options: _.union([property], draft.values?.touched?.options ?? []), fieldType: 'multiSelect' },
        };
      }

      if (value != null) {
        draft.values = {
          ...draft.values,
          [property]: value,
        };
      } else {
        removeField(draft, property);
        if (isGenericRecordDeletable(draft)) {
          deleteRecord();
        }
      }
    },
    elementKey
  );
}

// Returns 'prefilled' if the given property is not null and untouched.
export function getTouchedClassName<
  RECORD_TYPE extends RecordWithTouched,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(record: RECORD_TYPE, property: FIELD_KEY) {
  return !record.values?.touched?.options.includes(property) && record.values?.[property] != null ? 'prefilled' : '';
}

function writeTextFieldValue<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, value: string) {
  draft.values = {
    ...draft.values,
    [property]: {
      text: value,
      fieldType: 'text',
    },
  };
}

function writeNumericFieldValue<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, value: number) {
  draft.values = {
    ...draft.values,
    [property]: {
      number: value,
      fieldType: 'numeric',
    },
  };
}

function writeSingleSelectFieldValue<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, value: string) {
  draft.values = {
    ...draft.values,
    [property]: {
      option: value,
      fieldType: 'singleSelect',
    },
  };
}

function writeMultiSelectFieldValue<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY, values: readonly string[]) {
  draft.values = {
    ...draft.values,
    [property]: {
      options: [...values],
      fieldType: 'multiSelect',
    },
  };
}

function removeField<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY) {
  delete draft.values?.[property];
}

function isPropertyTouched<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY): boolean {
  return draft.values?.touched?.options.includes(property) ?? false;
}

function removeFromTouchedFields<
  RECORD_TYPE extends Draft<RecordWithTouched>,
  FIELD_KEY extends string & keyof (RECORD_TYPE & GenericElementEnteredValue)['values'],
>(draft: RECORD_TYPE, property: FIELD_KEY) {
  draft.values = {
    ...draft.values,
    touched: {
      options: _.filter(draft.values?.touched?.options ?? [], (element) => {
        return element !== property;
      }),
      fieldType: 'multiSelect',
    },
  };
}
